import React, { useState, useRef } from 'react';
import { 
  Radio, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, 
  Upload, Image, XCircle, Maximize2, X, FileText, 
  ExternalLink, Loader2, Eye, Download, Check, Save,
  Camera, Plus, Trash2, Star, Music, Ban
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';

const ElectronicsDetailModal = React.memo(({ 
  item, 
  items, 
  currentIndex, 
  onClose, 
  onSave, 
  onNavigate,
  formatCurrency,
  API_URL,
  userEmail,
  isNewItem = false
}) => {
  const getActualValue = (i) => {
    if (i.currentPrice && i.currentPrice > 0) return i.currentPrice;
    if (i.shares && i.shares > 1) return i.shares;
    return i.currentPrice || 0;
  };
  
  const actualValue = getActualValue(item);
  const actualCost = item.costBasis || 0;
  
  const [editForm, setEditForm] = useState({ 
    ...item,
    shares: actualValue
  });
  const [photos, setPhotos] = useState(item.photos || []);
  const [uploading, setUploading] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const fileInputRef = useRef(null);
  const [documents, setDocuments] = useState(item.documents || []);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [saving, setSaving] = useState(false);
  const docInputRef = useRef(null);
  const [viewingDocument, setViewingDocument] = useState(null);
  
  // Section collapse states
  const initialCollapseState = useRef(null);
  if (initialCollapseState.current === null) {
    try {
      const saved = localStorage.getItem('electronicsModalCollapseState');
      initialCollapseState.current = saved ? JSON.parse(saved) : { details: true, photos: true, documents: false };
    } catch (e) {
      initialCollapseState.current = { details: true, photos: true, documents: false };
    }
  }
  
  const [collapseState, setCollapseState] = useState(initialCollapseState.current);
  
  const updateCollapseState = (key, value) => {
    setCollapseState(prev => {
      const newState = { ...prev, [key]: value };
      setTimeout(() => {
        try { localStorage.setItem('electronicsModalCollapseState', JSON.stringify(newState)); } catch { /* ignore */ }
      }, 100);
      return newState;
    });
  };
  
  const detailsExpanded = collapseState.details;
  const photosExpanded = collapseState.photos;
  const documentsExpanded = collapseState.documents;
  
  const setDetailsExpanded = (val) => updateCollapseState('details', val);
  const setPhotosExpanded = (val) => updateCollapseState('photos', val);
  const setDocumentsExpanded = (val) => updateCollapseState('documents', val);

  // Initialize form with item data
  const itemId = item?.id;
  const itemUpdatedAt = item?.lastUpdated || item?.addedAt;
  React.useEffect(() => {
    const newValue = getActualValue(item);
    setEditForm({ ...item, shares: newValue });
    setPhotos(item.photos || []);
    setDocuments(item.documents || []);
  }, [itemId, itemUpdatedAt]);

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    try {
      const newPhotos = [...photos];
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', 'portfolio_electronics');
        
        const response = await fetch(`${API_URL}/api/upload/image`, {
          method: 'POST',
          body: formData,
        });
        
        if (response.ok) {
          const data = await response.json();
          newPhotos.push(data.url);
        }
      }
      setPhotos(newPhotos);
    } catch (error) {
      console.error('Error uploading photos:', error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeletePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleDocumentUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingDoc(true);
    try {
      const newDocs = [...documents];
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', 'portfolio_electronics_documents');
        
        const response = await fetch(`${API_URL}/api/upload/image`, {
          method: 'POST',
          body: formData,
        });
        
        if (response.ok) {
          const data = await response.json();
          newDocs.push({ url: data.url, name: file.name, uploadedAt: new Date().toISOString() });
        }
      }
      setDocuments(newDocs);
    } catch (error) {
      console.error('Error uploading documents:', error);
    } finally {
      setUploadingDoc(false);
      if (docInputRef.current) docInputRef.current.value = '';
    }
  };

  const handleDeleteDocument = (index) => {
    setDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async (closeAfterSave = false) => {
    setSaving(true);
    try {
      const updatedItem = {
        ...editForm,
        photos,
        documents,
        currentPrice: editForm.shares,
        investmentType: 'ELECTRONICS'
      };
      await onSave(updatedItem);
      if (closeAfterSave) onClose();
    } catch (error) {
      console.error('Error saving electronics:', error);
    } finally {
      setSaving(false);
    }
  };

  const canNavigate = items && items.length > 1;
  const itemName = editForm.symbol || `${editForm.electronicsBrand || ''} ${editForm.electronicsModel || ''}`.trim() || 'Audio Gear';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-cyan-600 to-cyan-700 text-white">
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-3">
              {canNavigate && (
                <div className="flex items-center gap-1 mr-2">
                  <button 
                    onClick={() => onNavigate && onNavigate('prev')} 
                    disabled={currentIndex === 0}
                    className={`p-1 rounded hover:bg-white/20 ${currentIndex === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="text-xs text-white/70">{currentIndex + 1}/{items.length}</span>
                  <button 
                    onClick={() => onNavigate && onNavigate('next')} 
                    disabled={currentIndex === items.length - 1}
                    className={`p-1 rounded hover:bg-white/20 ${currentIndex === items.length - 1 ? 'opacity-30 cursor-not-allowed' : ''}`}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Radio className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">{isNewItem ? 'Add New Audio Gear' : itemName}</h2>
                <div className="text-sm text-white/70">{editForm.electronicsType || 'Audio Equipment'}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isNewItem && (
                <button
                  onClick={(e) => { e.preventDefault(); setEditForm(prev => ({...prev, disposed: !prev.disposed})); }}
                  data-testid="disposed-toggle"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer ${editForm.disposed ? 'bg-red-500 text-white hover:bg-red-400' : 'bg-white/20 text-white hover:bg-white/30'}`}
                  title={editForm.disposed ? 'Mark as active' : 'Mark as sold/disposed'}
                >
                  <Ban className="w-4 h-4" /><span>{editForm.disposed ? 'Sold' : 'Active'}</span>
                </button>
              )}
              {!isNewItem && (
                <button
                  onClick={() => setEditForm({...editForm, isComplete: !editForm.isComplete})}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    editForm.isComplete 
                      ? 'bg-amber-500 text-amber-900' 
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                  title={editForm.isComplete ? 'Mark as incomplete' : 'Mark as complete'}
                >
                  <Star className={`w-4 h-4 ${editForm.isComplete ? 'fill-amber-900' : ''}`} />
                  <span>{editForm.isComplete ? 'Complete' : 'Incomplete'}</span>
                </button>
              )}
              <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full"><X className="w-5 h-5" /></button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className={`p-6 space-y-6 overflow-y-auto flex-1 ${editForm.disposed ? 'bg-gray-100 asset-sold' : ''}`}>
          {/* Photo Gallery */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <button onClick={() => setPhotosExpanded(!photosExpanded)} className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors">
              <span className="font-semibold text-slate-700 flex items-center gap-2">
                <Camera className="w-4 h-4" /> Photo Gallery
                {photos.length > 0 && <span className="bg-slate-200 text-slate-700 text-xs px-2 py-0.5 rounded-full">{photos.length}</span>}
              </span>
              <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${photosExpanded ? 'rotate-180' : ''}`} />
            </button>
            {photosExpanded && (
              <div className="p-4">
                <div className="flex flex-wrap gap-3">
                  {photos.map((photo, idx) => (
                    <div key={idx} className="relative group">
                      <div className="w-24 h-24 rounded-lg overflow-hidden bg-slate-100 cursor-pointer border-2 border-transparent hover:border-cyan-400 transition-colors" onClick={() => setLightboxIndex(idx)}>
                        <img src={photo} alt={`Item ${idx + 1}`} className="w-full h-full object-cover" />
                      </div>
                      <button onClick={() => handleDeletePhoto(idx)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  <label className="w-24 h-24 rounded-lg border-2 border-dashed border-slate-300 hover:border-cyan-400 flex flex-col items-center justify-center cursor-pointer bg-slate-50 hover:bg-slate-100 transition-colors">
                    <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" />
                    {uploading ? <div className="animate-spin w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full" /> : <><Plus className="w-6 h-6 text-slate-400" /><span className="text-xs text-slate-400 mt-1">Add</span></>}
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Lightbox */}
          {lightboxIndex !== null && (
            <div className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center" onClick={() => setLightboxIndex(null)}>
              <button onClick={(e) => { e.stopPropagation(); setLightboxIndex(Math.max(0, lightboxIndex - 1)); }} disabled={lightboxIndex === 0} className={`absolute left-4 p-2 rounded-full bg-white/10 hover:bg-white/20 ${lightboxIndex === 0 ? 'opacity-30' : ''}`}>
                <ChevronLeft className="w-8 h-8 text-white" />
              </button>
              <img src={photos[lightboxIndex]} alt="Item" className="max-w-[90vw] max-h-[90vh] object-contain" onClick={(e) => e.stopPropagation()} />
              <button onClick={(e) => { e.stopPropagation(); setLightboxIndex(Math.min(photos.length - 1, lightboxIndex + 1)); }} disabled={lightboxIndex === photos.length - 1} className={`absolute right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 ${lightboxIndex === photos.length - 1 ? 'opacity-30' : ''}`}>
                <ChevronRight className="w-8 h-8 text-white" />
              </button>
              <button onClick={() => setLightboxIndex(null)} className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20"><X className="w-6 h-6 text-white" /></button>
              <div className="absolute bottom-4 text-white text-sm">{lightboxIndex + 1} / {photos.length}</div>
            </div>
          )}

          {/* Equipment Details */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <button onClick={() => setDetailsExpanded(!detailsExpanded)} className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors">
              <span className="font-semibold text-slate-700 flex items-center gap-2"><Radio className="w-4 h-4" /> Equipment Details</span>
              <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${detailsExpanded ? 'rotate-180' : ''}`} />
            </button>
            {detailsExpanded && (
              <div className="p-4 space-y-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Item Name *</label>
                    <input type="text" value={editForm.symbol || ''} onChange={(e) => setEditForm({...editForm, symbol: e.target.value})} placeholder="e.g. McIntosh MC275" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Brand</label>
                    <input type="text" value={editForm.electronicsBrand || ''} onChange={(e) => setEditForm({...editForm, electronicsBrand: e.target.value})} placeholder="e.g. McIntosh, Marantz" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Model</label>
                    <input type="text" value={editForm.electronicsModel || ''} onChange={(e) => setEditForm({...editForm, electronicsModel: e.target.value})} placeholder="e.g. MC275, 2270" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Serial Number</label>
                    <input type="text" value={editForm.serialNumber || ''} onChange={(e) => setEditForm({...editForm, serialNumber: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Component Type</label>
                    <select value={editForm.electronicsType || ''} onChange={(e) => setEditForm({...editForm, electronicsType: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500">
                      <option value="">Select Type</option>
                      <option value="Amplifier">Amplifier</option>
                      <option value="Preamp">Preamp</option>
                      <option value="DAC">DAC</option>
                      <option value="Streamer">Streamer</option>
                      <option value="Receiver">Receiver</option>
                      <option value="Turntable">Turntable</option>
                      <option value="CD Player">CD Player</option>
                      <option value="Speakers">Speakers</option>
                      <option value="Subwoofer">Subwoofer</option>
                      <option value="Headphones">Headphones</option>
                      <option value="Headphone Amp">Headphone Amp</option>
                      <option value="Phono Stage">Phono Stage</option>
                      <option value="Tuner">Tuner</option>
                      <option value="Tape Deck">Tape Deck</option>
                      <option value="Cables">Cables</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Year</label>
                    <input type="text" value={editForm.electronicsYear || ''} onChange={(e) => setEditForm({...editForm, electronicsYear: e.target.value})} placeholder="e.g. 1970, ~1985" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Power / Main Spec</label>
                    <input type="text" value={editForm.electronicsPower || ''} onChange={(e) => setEditForm({...editForm, electronicsPower: e.target.value})} placeholder="e.g. 75W/ch, DSD DAC" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Inputs</label>
                    <input type="text" value={editForm.electronicsInputs || ''} onChange={(e) => setEditForm({...editForm, electronicsInputs: e.target.value})} placeholder="e.g. XLR, RCA, USB" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Color / Finish</label>
                    <input type="text" value={editForm.electronicsColor || ''} onChange={(e) => setEditForm({...editForm, electronicsColor: e.target.value})} placeholder="e.g. Black, Silver, Wood" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Condition</label>
                    <select value={editForm.condition || ''} onChange={(e) => setEditForm({...editForm, condition: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500">
                      <option value="">Select Condition</option>
                      <option value="Mint">Mint</option>
                      <option value="Excellent">Excellent</option>
                      <option value="Very Good">Very Good</option>
                      <option value="Good">Good</option>
                      <option value="Fair">Fair</option>
                      <option value="Poor">Poor</option>
                      <option value="For Parts">For Parts</option>
                    </select>
                  </div>
                </div>

                {/* Purchase & Value */}
                <div className="border-t border-slate-200 pt-4 mt-4">
                  <h4 className="text-sm font-medium text-slate-600 mb-3">💰 Purchase & Value</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Purchase Date</label>
                      <input type="date" value={editForm.purchaseDate || ''} onChange={(e) => setEditForm({...editForm, purchaseDate: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500" />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Purchase Price</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                        <input type="text" value={editForm.costBasis ? Math.round(editForm.costBasis).toLocaleString() : ''} onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          setEditForm({...editForm, costBasis: parseInt(val) || 0});
                        }} className="w-full pl-7 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Where Bought</label>
                      <select value={editForm.purchaseLocation || ''} onChange={(e) => setEditForm({...editForm, purchaseLocation: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500">
                        <option value="">Select</option>
                        <option value="Dealer">Dealer</option>
                        <option value="Private Sale">Private Sale</option>
                        <option value="eBay">eBay</option>
                        <option value="Audiogon">Audiogon</option>
                        <option value="US Audio Mart">US Audio Mart</option>
                        <option value="Retail Store">Retail Store</option>
                        <option value="Estate Sale">Estate Sale</option>
                        <option value="Inherited">Inherited</option>
                        <option value="Gift">Gift</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Current Estimated Value</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                        <input type="text" value={editForm.shares ? Math.round(editForm.shares).toLocaleString() : ''} onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          setEditForm({...editForm, shares: parseInt(val) || 0});
                        }} className="w-full pl-7 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Insured?</label>
                      <select value={editForm.isInsured || ''} onChange={(e) => setEditForm({...editForm, isInsured: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500">
                        <option value="">Select</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Location</label>
                      <input type="text" value={editForm.platform || ''} onChange={(e) => setEditForm({...editForm, platform: e.target.value})} placeholder="e.g. Living Room, Office" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500" />
                    </div>
                  </div>
                </div>

                {/* Extras */}
                <div className="border-t border-slate-200 pt-4 mt-4">
                  <h4 className="text-sm font-medium text-slate-600 mb-3">📦 Extras</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Original Box?</label>
                      <select value={editForm.hasOriginalBox || ''} onChange={(e) => setEditForm({...editForm, hasOriginalBox: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500">
                        <option value="">Select</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Remote Included?</label>
                      <select value={editForm.hasRemote || ''} onChange={(e) => setEditForm({...editForm, hasRemote: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500">
                        <option value="">Select</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                        <option value="N/A">N/A</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div className="border-t border-slate-200 pt-4 mt-4">
                  <label className="block text-xs text-slate-500 mb-1">Notes</label>
                  <textarea 
                    value={editForm.notes || ''} 
                    onChange={(e) => setEditForm({...editForm, notes: e.target.value})} 
                    placeholder="Add any notes about mods, service history, special details..."
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Documents */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <button onClick={() => setDocumentsExpanded(!documentsExpanded)} className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors">
              <span className="font-semibold text-slate-700 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Documents
                {documents.length > 0 && <span className="bg-slate-200 text-slate-700 text-xs px-2 py-0.5 rounded-full">{documents.length}</span>}
              </span>
              <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${documentsExpanded ? 'rotate-180' : ''}`} />
            </button>
            {documentsExpanded && (
              <div className="p-4">
                <div className="space-y-2">
                  {documents.map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg group">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-700 truncate max-w-[200px]">{doc.name || 'Document'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => setViewingDocument(doc)} className="p-1 hover:bg-slate-200 rounded"><Eye className="w-4 h-4 text-slate-500" /></button>
                        <a href={doc.url} target="_blank" rel="noopener noreferrer" className="p-1 hover:bg-slate-200 rounded"><Download className="w-4 h-4 text-slate-500" /></a>
                        <button onClick={() => handleDeleteDocument(idx)} className="p-1 hover:bg-red-100 rounded opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-4 h-4 text-red-500" /></button>
                      </div>
                    </div>
                  ))}
                  <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-cyan-400 hover:bg-slate-50 transition-colors">
                    <input ref={docInputRef} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" multiple onChange={handleDocumentUpload} className="hidden" />
                    {uploadingDoc ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : <><Plus className="w-5 h-5 text-slate-400" /><span className="text-sm text-slate-500">Add Document</span></>}
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Document Viewer */}
          {viewingDocument && (
            <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4" onClick={() => setViewingDocument(null)}>
              <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-3 border-b">
                  <span className="font-medium text-slate-700">{viewingDocument.name}</span>
                  <div className="flex items-center gap-2">
                    <a href={viewingDocument.url} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-slate-100 rounded"><ExternalLink className="w-4 h-4" /></a>
                    <button onClick={() => setViewingDocument(null)} className="p-2 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="h-[70vh]">
                  {viewingDocument.url?.toLowerCase().endsWith('.pdf') ? (
                    <iframe src={viewingDocument.url} className="w-full h-full" title="Document viewer" />
                  ) : (
                    <img src={viewingDocument.url} alt={viewingDocument.name} className="w-full h-full object-contain" />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t bg-slate-50">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="outline" onClick={() => handleSave(false)} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
          <Button onClick={() => handleSave(true)} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
            Save & Close
          </Button>
        </div>
      </div>
    </div>
  );
});

export default ElectronicsDetailModal;
